/**
 * Stub screens (F0). Every GDD §4.5 screen is registered from day one with this placeholder so routes, deep links,
 * `listScreens()` and captures work before the owning feature lands. Owners replace their entries with real screens.
 * Owner: UI/Visual (F3) restyled it with the kit: a gilt-framed lapis miniature with the title, then a Rubricated
 * page with the scribe's note. Features only call {@link stubScreen}.
 */
import type { Screen, ScreenContext, ScreenDef, ScreenId, ScreenKind, ScreenParams, ScreenScope, TabId } from '../shared';
import { disposeTree, h } from './dom';
import { installKit } from './kit/install';

export interface StubScreenOpts {
  id: ScreenId;
  route?: string;
  aliases?: string[];
  titleKey: string;
  needs3d: boolean;
  kind: ScreenKind;
  scope?: ScreenScope;
  tab?: TabId;
}

const STYLE_ID = 'mc-stub-style';
const STYLE = `
@layer screens {
.mc-stub{box-sizing:border-box;min-height:100%;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
  padding:calc(var(--safe-t,0px) + 24px) var(--gutter,16px) calc(var(--safe-b,0px) + 110px);color:var(--text-1)}
.mc-stub__folio{position:relative;width:min(100%,560px);display:grid;gap:26px}
.mc-stub__mini{position:relative;height:clamp(150px,38vw,230px);display:flex;align-items:center;justify-content:center;overflow:hidden;
  border-radius:50% 50% 0 0/30% 30% 0 0}
.mc-stub__mini.mc-frame9{border-radius:0}
.mc-stub__title{margin:0;padding:0 16px;text-align:center;font:400 clamp(24px,7.4vw,36px)/1.1 var(--font-display);letter-spacing:.03em;
  color:var(--mc-gold-light);-webkit-text-stroke:1px var(--mc-ink-deep);paint-order:stroke fill;text-shadow:0 2px 0 var(--mc-ink-deep)}
.mc-stub__body{font-size:var(--fs-read);line-height:var(--lh-read)}
.mc-stub__body p{margin:0 0 10px}
.mc-stub__meta{margin-top:12px;font-size:var(--fs-caption);color:var(--text-2);word-break:break-word}
.mc-stub__meta code{font-family:ui-monospace,Consolas,monospace;font-size:14px;color:var(--text-rubric)}
}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = STYLE;
  document.head.appendChild(s);
}

/** Passes every query parameter through as a string (stubs accept anything; real screens validate). */
function passThroughParams(q: URLSearchParams): ScreenParams {
  const out: ScreenParams = {};
  q.forEach((v, k) => { out[k] = v; });
  return out;
}

/** A placeholder `ScreenDef`: an illuminated folio with the screen's Russian title, its route and the parameters. */
export function stubScreen(o: StubScreenOpts): ScreenDef {
  return {
    id: o.id,
    route: o.route,
    aliases: o.aliases,
    titleKey: o.titleKey,
    needs3d: o.needs3d,
    kind: o.kind,
    scope: o.scope ?? 'P0',
    tab: o.tab,
    parseParams: passThroughParams,
    create(ctx: ScreenContext, params: ScreenParams): Screen {
      ensureStyle();
      void installKit();
      const shown = Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}=${String(v)}`).join(' · ');
      const root = h('section', { class: 'mc-stub', attrs: { 'data-screen-stub': o.id, 'aria-labelledby': `stub-${o.id}` } },
        h('div', { class: 'mc-stub__folio' },
          h('div', { class: 'mc-stub__mini mc-vault mc-frame9 mc-tier-illum', attrs: { 'data-frame': 'gilt', 'data-orn': 'frame' } },
            h('h1', { class: 'mc-stub__title', attrs: { id: `stub-${o.id}` } }, ctx.t(o.titleKey))),
          h('div', { class: 'mc-stub__body mc-tier-rubric', attrs: { 'data-orn': 'penwork' } },
            h('p', { class: 'mc-dropcap mc-read', attrs: { 'data-orn': 'dropcap' } }, ctx.t('ui.stub.lead')),
            h('p', null, ctx.t(o.scope && o.scope !== 'P0' ? 'ui.stub.later' : 'ui.stub.soon')),
            h('div', { class: 'mc-stub__meta' },
              h('code', null, o.id), o.route ? ' · ' : '', o.route ? h('code', null, o.route) : '',
              shown ? h('div', null, shown) : null))));
      return {
        root,
        isIdle: () => true,
        debugState: () => ({ stub: true, id: o.id, params }),
        dispose: () => disposeTree(root),
      };
    },
  };
}
